import React, { useMemo } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { BillingFinancialOverview } from './billing-dashboard.types';
import { formatBillingCurrency, formatBillingDateTime } from './billing-dashboard-format';

type Props = {
  overview: BillingFinancialOverview;
};

function formatPercentChange(value: number | null): string {
  if (value == null || Number.isNaN(value)) {
    return '—';
  }
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toFixed(1)}%`;
}

const BillingFinancialOverviewSection: React.FC<Props> = ({ overview }) => {
  const barChartData = useMemo(
    () =>
      overview.monthlySeries.map((m) => ({
        period: m.monthLabel,
        Invoiced: m.invoiced,
        Collected: m.collected,
      })),
    [overview.monthlySeries]
  );

  return (
    <section
      className="rounded-xl border border-gray-200 bg-white p-4 sm:p-5"
      aria-labelledby="billing-overall-data-heading"
    >
      <h2
        id="billing-overall-data-heading"
        className="mb-1 text-base font-semibold text-gray-900"
      >
        Overall data
      </h2>
      <div className="mb-4">
        <p className="text-sm text-gray-600">
          Month-over-month performance and balances for the billing programme.
        </p>
        {overview.snapshotUpdatedAt ? (
          <p className="mt-1 text-xs text-gray-500">
            Data as of
            {' '}
            {formatBillingDateTime(overview.snapshotUpdatedAt)}
          </p>
        ) : null}
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-xl border border-gray-200 bg-gray-50/90 p-4">
          <p className="text-xs font-medium text-gray-600">
            Amount invoiced
          </p>
          <p className="mt-1 text-xl font-bold text-gray-900 tabular-nums">
            {formatBillingCurrency(overview.totalBilledCurrentPeriod)}
          </p>
          <p className="mt-1 text-xs text-gray-600">
            vs prior month:
            {' '}
            <span className="font-medium text-gray-800">
              {formatPercentChange(overview.invoicedPercentChangeVsPrior)}
            </span>
          </p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-gray-50/90 p-4">
          <p className="text-xs font-medium text-gray-600">
            Amount collected
          </p>
          <p className="mt-1 text-xl font-bold text-gray-900 tabular-nums">
            {formatBillingCurrency(overview.totalCollected)}
          </p>
          <p className="mt-1 text-xs text-gray-600">
            vs prior month:
            {' '}
            <span className="font-medium text-gray-800">
              {formatPercentChange(overview.collectedPercentChangeVsPrior)}
            </span>
          </p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-gray-50/90 p-4">
          <p className="text-xs font-medium text-gray-600">
            Outstanding
          </p>
          <p className="mt-1 text-xl font-bold text-gray-900 tabular-nums">
            {formatBillingCurrency(overview.outstandingBalance)}
          </p>
          <p className="mt-1 text-xs text-gray-500">
            Not yet collected
          </p>
        </div>
        <div className="rounded-xl border border-amber-100 bg-amber-50/70 p-4">
          <p className="text-xs font-medium text-amber-900/85">
            Overdue (past due)
          </p>
          <p className="mt-1 text-xl font-bold text-amber-950 tabular-nums">
            {formatBillingCurrency(overview.overdueAmount)}
          </p>
          <p className="mt-1 text-xs text-amber-900/70">
            Aged receivables
          </p>
        </div>
      </div>

      <div className="mt-6">
        <p className="mb-2 text-sm font-medium text-gray-800">
          Invoiced vs collected by month
        </p>
        <div className="h-44 w-full rounded-xl border border-gray-100 bg-gray-50/50 p-2">
          <ResponsiveContainer
            width="100%"
            height="100%"
          >
            <BarChart
              data={barChartData}
              margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                className="stroke-gray-200"
              />
              <XAxis
                dataKey="period"
                tick={{ fontSize: 11 }}
                className="text-gray-600"
              />
              <YAxis
                tick={{ fontSize: 10 }}
                tickFormatter={(v) =>
                  new Intl.NumberFormat('en-GB', {
                    notation: 'compact',
                    compactDisplay: 'short',
                  }).format(Number(v))
                }
                width={48}
              />
              <Tooltip
                formatter={(value: number) => formatBillingCurrency(value)}
                labelStyle={{ fontWeight: 600 }}
                contentStyle={{ fontSize: 12 }}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar
                dataKey="Invoiced"
                fill="#6366f1"
                radius={[6, 6, 0, 0]}
              />
              <Bar
                dataKey="Collected"
                fill="#22c55e"
                radius={[6, 6, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </section>
  );
};

export default BillingFinancialOverviewSection;
